/**
 * Ключи кэша TanStack Query.
 *
 * Собраны в одном месте, чтобы инвалидация после мутации не разъезжалась с
 * запросом: разъехавшийся ключ не ломает сборку, он просто перестаёт
 * обновлять экран, и заметно это только глазами.
 */

import type {
  CallFilters,
  ConversionFilters,
  CtSessionFilters,
  CtSourcesFilters,
  DeliveryFilters,
  DirectoryFilters,
  DisclosureFilters,
  EmployeeFilters,
  ReportFilters,
} from './types';

export const queryKeys = {
  me: ['me'] as const,

  units: ['units'] as const,

  sources: ['sources'] as const,
  bindings: (sourceId: string) => ['sources', sourceId, 'bindings'] as const,
  directory: (sourceId: string, kind: string, filters: DirectoryFilters) =>
    ['sources', sourceId, 'directory', kind, filters] as const,

  calls: (filters: CallFilters) => ['calls', filters] as const,
  call: (id: string) => ['calls', id] as const,
  callAudio: (id: string) => ['calls', id, 'audio'] as const,
  callComments: (id: string) => ['calls', id, 'comments'] as const,

  scorecards: ['scorecards'] as const,
  rules: (scorecardId: string) => ['scorecards', scorecardId, 'rules'] as const,

  users: ['users'] as const,
  servers: ['servers'] as const,
  disclosureGrants: (filters: DisclosureFilters) => ['disclosure-grants', filters] as const,

  employees: (filters: EmployeeFilters) => ['employees', filters] as const,

  ctPools: ['calltracking', 'pools'] as const,
  ctNumbers: (poolId: string | undefined) => ['calltracking', 'numbers', poolId ?? 'all'] as const,
  ctAvailableNumbers: ['calltracking', 'available-numbers'] as const,
  ctSites: ['calltracking', 'sites'] as const,
  ctStaticRules: (siteId: string | undefined) =>
    ['calltracking', 'static-rules', siteId ?? 'all'] as const,
  ctSessions: (filters: CtSessionFilters) => ['calltracking', 'sessions', filters] as const,
  callGoals: (status: string | undefined) => ['call-goals', status ?? 'all'] as const,
  adNames: (source: string | undefined) => ['calltracking', 'ad-names', source ?? 'all'] as const,
  unitAddenda: ['analysis-settings', 'units'] as const,
  unitAddendum: (unitId: string) => ['analysis-settings', 'units', unitId] as const,
  analysisInput: (callId: string) => ['calls', callId, 'analysis-input'] as const,
  ctHandlingReport: (filters: CtSourcesFilters) =>
    ['analytics', 'calltracking', 'handling', filters] as const,
  ctSourcesReport: (filters: CtSourcesFilters) =>
    ['analytics', 'calltracking', 'sources', filters] as const,
  ctSitesReport: (period: { from?: string; to?: string }) =>
    ['analytics', 'calltracking', 'sites', period] as const,

  integrations: ['integrations'] as const,
  integration: (id: string) => ['integrations', id] as const,
  /** Список собранных адаптеров живёт долго: он меняется только с релизом. */
  integrationKinds: ['integrations', 'kinds'] as const,
  integrationDictionaries: (id: string) => ['integrations', id, 'dictionaries'] as const,
  integrationMappings: (id: string) => ['integrations', id, 'mappings'] as const,
  outboundDeliveries: (filters: DeliveryFilters) =>
    ['integrations', 'deliveries', filters] as const,
  /** Период — часть данных: конверсия за сентябрь не годится за неделю. */
  conversion: (filters: ConversionFilters) => ['integrations', 'conversion', filters] as const,
  assignments: (employeeId: string) => ['employees', employeeId, 'assignments'] as const,

  /**
   * Отчёты. Фильтры входят в ключ целиком: период — часть данных, а не вид
   * их показа, и кэш «за сентябрь» не годится для «за неделю».
   */
  dashboard: (filters: ReportFilters, compare: boolean) =>
    ['analytics', 'dashboard', filters, compare] as const,
  summary: (filters: ReportFilters, groupBy: string) =>
    ['analytics', 'summary', filters, groupBy] as const,
  heatmap: (filters: ReportFilters) => ['analytics', 'heatmap', filters] as const,
  handoff: (filters: ReportFilters) => ['analytics', 'handoff', filters] as const,
  scoreDynamics: (filters: ReportFilters, bucket: string, groupBy: string) =>
    ['analytics', 'score-dynamics', filters, bucket, groupBy] as const,
  topViolations: (filters: ReportFilters, limit: number) =>
    ['analytics', 'top-violations', filters, limit] as const,
  violations: (filters: ReportFilters, groupBy: string) =>
    ['analytics', 'violations', filters, groupBy] as const,
  rating: (filters: ReportFilters, sortBy: string) =>
    ['analytics', 'rating', filters, sortBy] as const,
  employeeRules: (employeeId: string, filters: ReportFilters) =>
    ['analytics', 'rating', employeeId, 'rules', filters.from, filters.to] as const,
} as const;
